import Button from 'components/app-components/ui/button';

export interface RequirementGroup {
  /** Optional short heading shown above this group's items. */
  title?: string;
  /** Human-readable requirement labels for this group. */
  items: string[];
  /** Label for this group's action button. */
  actionLabel: string;
  onAction: () => void;
  isLoading?: boolean;
}

interface RequiredDocumentsBannerProps {
  groups: RequirementGroup[];
}

/**
 * Warns the user that Bridge needs additional verification to approve their card and lists
 * what they must complete. Requirement groups can resolve through different links (identity
 * via Bridge's KYC link, source of funds via Persona), so each group has its own action.
 */
const RequiredDocumentsBanner = ({ groups }: RequiredDocumentsBannerProps) => {
  if (groups.length === 0) return null;
  return (
    <div className="mb-6 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-6 py-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-amber-200 bg-amber-100">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-amber-600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="font-medium text-neutral-900">Additional verification required</p>
        <p className="mt-1 text-sm text-neutral-500">To finish verifying your card, please complete the following:</p>
        {groups.map((group) => (
          <div key={group.actionLabel} className="mt-3">
            {group.title && <p className="text-sm font-medium text-neutral-700">{group.title}</p>}
            <ul className="mt-1 list-inside list-disc text-sm text-neutral-600">
              {group.items.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
            <Button
              variant="primary"
              onClick={group.onAction}
              loading={group.isLoading}
              disabled={group.isLoading}
              className="mt-2"
            >
              {group.actionLabel}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RequiredDocumentsBanner;
